import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Check, ChevronDown, Trash2 } from 'lucide-react';
import { useCreateGuestTag, useDeleteGuestTag } from '../../api/hooks/useInvites';
import type { GuestTag } from '../../types';
import { TAG_BADGE_CLASS, getTagBg, getTagDot } from '../../utils/tagStyles';

interface TagSelectorProps {
  availableTags: GuestTag[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  label?: string;
  placeholder?: string;
  variant?: 'default' | 'builder';
}

const COLOR_OPTIONS = [
  { value: 'primary', bg: 'bg-primary/30', label: 'Primary' },
  { value: 'warning', bg: 'bg-amber-500/30', label: 'Warning' },
  { value: 'success', bg: 'bg-emerald-500/30', label: 'Success' },
  { value: 'error', bg: 'bg-red-500/30', label: 'Error' },
  { value: 'purple', bg: 'bg-purple-500/30', label: 'Purple' },
  { value: 'pink', bg: 'bg-pink-500/30', label: 'Pink' },
  { value: 'sky', bg: 'bg-sky-500/30', label: 'Sky' },
  { value: 'black', bg: 'bg-gray-900/30', label: 'Black' },
];

export function TagSelector({ availableTags, selectedTagIds, onChange, label, placeholder = 'Select tags...', variant = 'default' }: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('primary');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const createTag = useCreateGuestTag();
  const deleteTag = useDeleteGuestTag();
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  // Calculate portal position when opening
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const maxMenuH = 280;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUpward = spaceBelow < maxMenuH && spaceAbove > spaceBelow;
      setMenuPos({
        top: openUpward ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        openUpward,
      });
    }
  }, [isOpen]);

  // Close on click outside (container + portaled menu)
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inContainer && !inMenu) {
        setIsOpen(false);
        setIsCreating(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setIsCreating(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus name input when switching to create mode
  useEffect(() => {
    if (isCreating) {
      nameInputRef.current?.focus();
    }
  }, [isCreating]);

  const selectedTags = availableTags.filter((t) => selectedTagIds.includes(t.id));
  const unselectedTags = availableTags.filter((t) => !selectedTagIds.includes(t.id));

  const handleRemoveTag = (tagId: number) => {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleSelectTag = (tagId: number) => {
    onChange([...selectedTagIds, tagId]);
    setIsOpen(false);
  };

  const handleDeleteTag = async (tagId: number) => {
    if (deleteTag.isPending) return;
    try {
      await deleteTag.mutateAsync(tagId);
      onChange(selectedTagIds.filter((id) => id !== tagId));
      setConfirmDeleteId(null);
    } catch {
      // mutation error handled by react-query
    }
  };

  const handleCreateTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed || createTag.isPending) return;
    try {
      const tag = await createTag.mutateAsync({ name: trimmed, color: newTagColor });
      onChange([...selectedTagIds, tag.id]);
      setNewTagName('');
      setNewTagColor('primary');
      setIsCreating(false);
      setIsOpen(false);
    } catch {
      // mutation error handled by react-query
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="block text-xs font-medium text-text-secondary mb-1">{label}</label>}

      {/* Input area */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { setIsOpen(!isOpen); if (isOpen) setIsCreating(false); }}
        data-builder="tag-selector-trigger"
        className={`relative w-full min-h-[40px] py-2 px-4 pr-10 bg-background-card border text-sm ${
          isOpen ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary'
        } ${variant === 'builder' ? 'rounded-lg' : 'rounded-md'} text-left cursor-pointer flex flex-wrap items-center gap-1.5 transition-all duration-200`}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={`${TAG_BADGE_CLASS} gap-1 ${getTagBg(tag.color)}`}
          >
            {tag.name}
            <span
              role="button"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleRemoveTag(tag.id);
              }}
              className="hover:opacity-70 transition-opacity"
            >
              <X size={12} />
            </span>
          </span>
        ))}
        {selectedTags.length === 0 && (
          <span className="text-text-secondary">{placeholder}</span>
        )}
        <ChevronDown
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown — portalled to avoid overflow clipping */}
      {createPortal(
        <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: menuPos.openUpward ? 10 : -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: menuPos.openUpward ? 10 : -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[9999] bg-background-card border border-border rounded-lg shadow-lg overflow-hidden"
            style={{
              top: menuPos.openUpward ? undefined : menuPos.top,
              bottom: menuPos.openUpward ? window.innerHeight - menuPos.top : undefined,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            {/* Available tags list */}
            {!isCreating && (
              <div className="py-1 max-h-60 overflow-y-auto">
                {unselectedTags.length === 0 && (
                  <div className="px-4 py-2.5 text-sm text-text-tertiary">
                    No more tags available
                  </div>
                )}
                {unselectedTags.map((tag) => (
                  <div key={tag.id} className="flex items-center group">
                    {confirmDeleteId === tag.id ? (
                      <div className="flex-1 flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-text-secondary">Delete "{tag.name}"?</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTag(tag.id)}
                            className="text-xs text-error hover:text-error/80 font-medium transition-colors"
                          >
                            {deleteTag.isPending ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleSelectTag(tag.id)}
                          data-builder={`tag-selector-select-${tag.id}`}
                          className="flex-1 text-left px-4 py-2.5 text-sm text-text-primary hover:bg-background-surface transition-colors flex items-center gap-3 cursor-pointer"
                        >
                          <span className={`w-3 h-3 rounded-full shrink-0 ${getTagDot(tag.color)}`} />
                          {tag.name}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(tag.id);
                          }}
                          data-builder={`tag-selector-delete-${tag.id}`}
                          className="px-3 py-2.5 text-text-secondary opacity-0 group-hover:opacity-100 hover:text-error transition-all"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Create new tag form */}
            {isCreating ? (
              <div className="p-3 space-y-3 border-t border-border">
                <input
                  ref={nameInputRef}
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateTag();
                    }
                  }}
                  placeholder="Tag name"
                  data-builder="tag-selector-name-input"
                  className="w-full px-3 py-2 bg-background-card border border-border rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
                <div className="flex items-center gap-1.5">
                  {COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewTagColor(opt.value)}
                      data-builder={`tag-selector-color-${opt.value}`}
                      className={`w-6 h-6 rounded-full ${opt.bg} flex items-center justify-center transition-transform ${
                        newTagColor === opt.value ? 'ring-2 ring-offset-2 ring-offset-background-card ring-primary scale-110' : 'hover:scale-110'
                      }`}
                    >
                      {newTagColor === opt.value && <Check size={12} className="text-text-primary" />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewTagName('');
                      setNewTagColor('primary');
                    }}
                    data-builder="tag-selector-create-cancel"
                    className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim() || createTag.isPending}
                    data-builder="tag-selector-create-submit"
                    className="flex-1 px-3 py-1.5 text-sm bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
                  >
                    {createTag.isPending ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                data-builder="tag-selector-add-new"
                className="w-full text-left px-4 py-2.5 text-sm text-primary hover:bg-background-surface transition-colors flex items-center gap-3 border-t border-border cursor-pointer"
              >
                <Plus size={14} />
                Add new tag
              </button>
            )}
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
